import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import useApiCall from "../common/api-call";
import {
  listAnnounce,
  getAnnounce,
  upsertAnnounce,
  deleteAnnounce,
} from "./notification-service";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";

const NotificationListView = () => {
  const { apiCall } = useApiCall();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    id: null,
    createdBy: "",
    createdAt: "",
  });
  const [originalFormData, setOriginalFormData] = useState({
    title: "",
    content: "",
    id: null,
    createdBy: "",
    createdAt: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [titleError, setTitleError] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [contentTouched, setContentTouched] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    const res = await listAnnounce(apiCall);
    setNotifications(res?.items || res || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleShowDetail = async item => {
    const detail = await getAnnounce(apiCall, item.id);
    setSelected(detail || item);
    setDetailOpen(true);
  };

  const handleOpenForm = async (item = null) => {
    if (item) {
      setActionLoading("edit");
      try {
        const detail = await getAnnounce(apiCall, item.id);
        setFormData({
          id: item.id,
          title: item.title,
          content: detail?.content || "",
          createdBy: item.createdBy,
          createdAt: item.createdAt,
        });
        setOriginalFormData({
          id: item.id,
          title: item.title,
          content: detail?.content || "",
          createdBy: item.createdBy,
          createdAt: item.createdAt,
        });
        setEditMode(true);
      } finally {
        setActionLoading(null);
      }
    } else {
      setFormData({
        id: null,
        title: "",
        content: "",
        createdBy: "",
        createdAt: "",
      });
      setOriginalFormData({
        id: null,
        title: "",
        content: "",
        createdBy: "",
        createdAt: "",
      });
      setEditMode(false);
    }
    setTitleError("");
    setTitleTouched(false);
    setContentTouched(false);
    setFormOpen(true);
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === "title") {
      setTitleTouched(true);
      if (!editMode && notifications.some(n => n.title === value)) {
        setTitleError("Title already exists");
      } else if (
        editMode &&
        notifications.some(n => n.title === value && n.id !== formData.id)
      ) {
        setTitleError("Title already exists");
      } else {
        setTitleError("");
      }
    }
    if (name === "content") {
      setContentTouched(true);
    }
  };

  const isFormChanged = () =>
    formData.title !== originalFormData.title ||
    formData.content !== originalFormData.content;

  const isAddValid = () => {
    if (!formData.title.trim() || !formData.content.trim()) return false;
    if (notifications.some(n => n.title === formData.title)) return false;
    return true;
  };

  const isEditValid = () => {
    if (!isFormChanged()) return false;
    if (!formData.title.trim() || !formData.content.trim()) return false;
    if (
      notifications.some(
        n => n.title === formData.title && n.id !== formData.id,
      )
    )
      return false;
    return true;
  };

  const handleFormSubmit = async () => {
    setActionLoading(formData.id ? "save" : "add");
    await upsertAnnounce(
      apiCall,
      formData.id,
      formData.title,
      formData.content,
    );
    setActionLoading(null);
    setFormOpen(false);
    fetchNotifications();
  };

  const handleCancelForm = () => {
    setFormOpen(false);
    setTitleError("");
    setTitleTouched(false);
    setContentTouched(false);
  };

  const handleAskDelete = item => {
    setDeleteTarget(item);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setActionLoading("delete");
    await deleteAnnounce(apiCall, deleteTarget.id);
    setActionLoading(null);
    setConfirmDeleteOpen(false);
    setDeleteTarget(null);
    fetchNotifications();
    setDetailOpen(false);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh" }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          disabled={!!actionLoading}
        >
          Add Notification
          {actionLoading === "add" && (
            <CircularProgress size={20} sx={{ ml: 1 }} />
          )}
        </Button>
      </Stack>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, ml: 2 }}>
          No notifications yet, please add one.
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: 1, boxShadow: 1 }}
        >
          <Table sx={{ minWidth: 500 }} size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Title</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map(item => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={item.title || ""} placement="top">
                      <Typography noWrap>{item.title}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={item.createdBy || ""} placement="top">
                      <Typography noWrap>{item.createdBy}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={item.createdAt || ""} placement="top">
                      <Typography noWrap>
                        {item.createdAt
                          ? dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss")
                          : ""}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Edit" placement="top">
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenForm(item)}
                            disabled={!!actionLoading}
                          >
                            Edit
                            {actionLoading === "save" &&
                              formData.id === item.id && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )}
                            {actionLoading === "edit" &&
                              formData.id === item.id && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete" placement="top">
                        <span>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleAskDelete(item)}
                            disabled={!!actionLoading}
                          >
                            Delete
                            {actionLoading === "delete" &&
                              selected?.id === item.id && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Notification Detail</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2, px: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selected?.title}
          </Typography>
          <Typography variant="body1">{selected?.content}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailOpen(false)}
            disabled={!!actionLoading}
          >
            Close
          </Button>
          <Button
            color="error"
            onClick={() => handleAskDelete(selected)}
            disabled={!!actionLoading}
          >
            Delete
            {actionLoading === "delete" && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Button>
          <Button
            onClick={() => {
              setDetailOpen(false);
              handleOpenForm(selected);
            }}
            disabled={!!actionLoading}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Notification" : "Add Notification"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2, px: 3 }}>
          <TextField
            margin="normal"
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleFormChange}
            fullWidth
            error={!!titleError || (titleTouched && !formData.title.trim())}
            helperText={
              titleError ||
              (titleTouched && !formData.title.trim()
                ? "Title cannot be empty"
                : "")
            }
          />
          <TextField
            margin="normal"
            label="Content"
            name="content"
            value={formData.content}
            onChange={handleFormChange}
            fullWidth
            multiline
            minRows={4}
            error={contentTouched && !formData.content.trim()}
            helperText={
              contentTouched && !formData.content.trim()
                ? "Content cannot be empty"
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={handleCancelForm}
            disabled={!!actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={
              !!actionLoading || (editMode ? !isEditValid() : !isAddValid())
            }
          >
            {editMode ? "Save" : "Add"}
            {(actionLoading === "save" || actionLoading === "add") && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this notification?
          </Typography>
          <Typography sx={{ mt: 1, color: "text.secondary" }}>
            {deleteTarget?.title}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={!!actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={!!actionLoading}
          >
            Confirm
            {actionLoading === "delete" && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationListView;
